import { useEffect, useState, useCallback } from "react";
import { api, getToken, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, MapPin, FileText, Pencil, Trash2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useExports } from "@/context/ExportsContext";

const EMPTY = { nome: "", citta: "", indirizzo: "", telefono: "" };

export default function Sedi() {
  const [sedi, setSedi] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { startExport } = useExports();

  const load = useCallback(() => { api.get("/sedi").then((r) => setSedi(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const reportPerSede = () => startExport("reports_per_sede", {}, "Report per Sede");

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ nome: s.nome, citta: s.citta || "", indirizzo: s.indirizzo || "", telefono: s.telefono || "" }); setOpen(true); };

  const save = async () => {
    if (!form.nome.trim()) { toast.error("Inserisci il nome della sede"); return; }
    try {
      if (editing) { await api.put(`/sedi/${editing.id}`, form); toast.success("Sede aggiornata"); }
      else { await api.post("/sedi", form); toast.success("Sede creata"); }
      setOpen(false); load();
    } catch (e) { toast.error("Errore"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare la sede "${s.nome}"? Le pratiche collegate resteranno senza sede.`)) return;
    await api.delete(`/sedi/${s.id}`); load(); toast.success("Sede eliminata");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Sedi</h1>
          <p className="text-sm text-muted-foreground mt-1">{sedi.length} sedi della tua attività</p>
        </div>
        <div className="flex gap-2">
        <Button variant="outline" data-testid="sede-report-btn" onClick={reportPerSede}><FileSpreadsheet className="h-4 w-4 mr-2" /> Report per Sede</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-sede-btn" onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Nuova Sede</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Modifica Sede" : "Nuova Sede"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Nome sede *</Label><Input data-testid="sede-nome-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="es. Latina" /></div>
              <div className="space-y-1.5"><Label>Città</Label><Input data-testid="sede-citta-input" value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Indirizzo</Label><Input data-testid="sede-indirizzo-input" value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} placeholder="es. Via Roma 10" /></div>
              <div className="space-y-1.5"><Label>Telefono</Label><Input data-testid="sede-telefono-input" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="es. 0773 489909" />
                <p className="text-xs text-muted-foreground">Usato nei WhatsApp di conferma e promemoria appuntamenti.</p></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid="sede-submit-btn" onClick={save}>{editing ? "Salva" : "Crea"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sedi.map((s) => (
          <Card key={s.id} data-testid={`sede-card-${s.id}`} className="p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0"><MapPin className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{s.nome}</p>
                {s.indirizzo && <p className="text-xs text-muted-foreground mt-0.5 truncate">{s.indirizzo}</p>}
                {s.telefono && <p className="text-xs text-muted-foreground mt-0.5 truncate" data-testid={`sede-telefono-${s.id}`}>Tel. {s.telefono}</p>}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" data-testid={`sede-edit-${s.id}`} onClick={() => openEdit(s)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" data-testid={`sede-delete-${s.id}`} onClick={() => remove(s)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-border flex items-center gap-1.5 text-xs text-muted-foreground">
              <FileText className="h-3.5 w-3.5" /> {s.practice_count} pratiche di provenienza
            </div>
          </Card>
        ))}
        {sedi.length === 0 && (
          <Card className="p-12 text-center text-muted-foreground col-span-full">
            <MapPin className="h-8 w-8 mx-auto mb-2 opacity-40" />Nessuna sede registrata
          </Card>
        )}
      </div>
    </div>
  );
}
